import React, { useCallback, useEffect } from "react";
import type { ConfettiHandle } from "@/components/Confetti/Confetti";
import { useRampHaptic } from "@/utils/hooks/useRampHaptic";

const rand = (min: number, max: number) => Math.random() * (max - min) + min;
const randi = (min: number, max: number) => Math.floor(rand(min, max + 1));

export function useConfettiShow(confettiRef: React.RefObject<ConfettiHandle>) {
  const { ramp } = useRampHaptic();
  const fireFromCorners = useCallback(() => {
    const w = window.innerWidth;
    const h = window.innerHeight;

    confettiRef.current?.shoot({
      x: 0,
      y: h + 20,
      angle: 70,
      spread: 30,
      startVelocity: 60,
      count: 200,
      colors: ["#FFD60A", "#4D96FF", "#6BCB77"],
    });

    confettiRef.current?.shoot({
      x: w,
      y: h + 20,
      angle: 110,
      spread: 30,
      startVelocity: 60,
      count: 200,
      colors: ["#FF6B6B", "#FFC300", "#845EC2"],
    });
  }, [confettiRef]);

  const randomRoundFirework = useCallback(() => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const baseX = rand(0.15 * w, 0.85 * w);
    const baseY = rand(0.25 * h, 0.65 * h);
    const colors = ["#FFD60A", "#FF6B6B", "#4D96FF", "#6BCB77", "#845EC2"];

    setTimeout(() => {
      const r = rand(0, 60);
      const t = rand(0, Math.PI * 2);
      const x = baseX + Math.cos(t) * r;
      const y = baseY + Math.sin(t) * r;

      confettiRef.current?.shoot({
        x,
        y,
        spread: 360,
        startVelocity: rand(25, 45),
        count: randi(60, 100),
        ticks: 220,
        scalar: rand(0.4, 0.6),
        colors,
      });

      ramp(0, 1);
    }, 200);
  }, [confettiRef, ramp]);

  const fireworkParty = useCallback(
    (total = 6, interval = 500) => {
      for (let i = 0; i < total; i += 1) {
        setTimeout(() => randomRoundFirework(), i * interval);
      }
    },
    [randomRoundFirework]
  );

  useEffect(() => {
    const unlock = () => {
      // dummy canvas draw to activate rAF / GPU
      const c = document.createElement("canvas");
      const ctx = c.getContext("2d");
      ctx?.fillRect(0, 0, 1, 1);
      document.body.removeEventListener("touchstart", unlock);
      document.body.removeEventListener("click", unlock);
    };
    document.body.addEventListener("touchstart", unlock, { passive: true });
    document.body.addEventListener("click", unlock, { passive: true });
  }, []);

  return {
    fireFromCorners,
    randomRoundFirework,
    fireworkParty,
  };
}
